// src/components/GameVisibility.tsx
//
// Show / hide individual games on the main website (growl.games), and mark
// games as PRIORITY within their own provider's section (the system
// previously hand-edited in the gis_prioritized_games DB table via Adminer —
// game_ids is an ordered CSV per vendor; getGamesgis shows those games first,
// in that order, ahead of the regular alphabetical results for that provider).
// Lists the full GIS catalog (incl. hidden games) with thumbnails. Backed by:
//   GET   /api/gis/admin/gis/games?provider=&type=&search=&hidden=&page=&limit=
//   PATCH /api/gis/admin/gis/games/:uuid/visibility   body: { hidden: boolean }
//   GET   /api/gis/admin/gis/priorities                -> [{ vendor, gameIds[] }]
//   PATCH /api/gis/admin/gis/games/:uuid/priority      body: { prioritized: boolean }
// All protected by the staff JWT (apiFetch attaches it automatically).

import React, { useEffect, useState, useCallback, useRef } from "react";
import { RefreshCw, Search, Eye, EyeOff, ChevronLeft, ChevronRight, Loader2, Heart } from "lucide-react";
import { apiFetch } from "../utils/api";

type Game = {
  uuid: string;
  name: string;
  provider: string;
  label?: string;
  type?: string;
  image?: string;
  images?: { url?: string }[];
  is_hidden?: boolean;
};

type GamesResponse = {
  success: boolean;
  data: Game[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
};

type PrioritiesResponse = {
  success: boolean;
  data: { vendor: string; gameIds: string[] }[];
};

const PAGE_SIZE = 48;

// Neutral placeholder shown when a thumbnail is missing / fails to load.
const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="80" viewBox="0 0 120 80">
       <rect width="120" height="80" fill="#e5e7eb"/>
       <path d="M46 30h28a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4H46a4 4 0 0 1-4-4V34a4 4 0 0 1 4-4Zm6 8a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm16 0a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" fill="#9ca3af"/>
     </svg>`
  );

const gameImage = (g: Game) =>
  g.image || (g.images && g.images[0] && g.images[0].url) || FALLBACK_IMG;

export default function GameVisibility() {
  const [games, setGames] = useState<Game[]>([]);
  const [providers, setProviders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState<"" | "false" | "true">(""); // "" all, "false" visible, "true" hidden

  // pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // per-row toggles in flight
  const [savingUuid, setSavingUuid] = useState<string | null>(null);
  const [savingPriorityUuid, setSavingPriorityUuid] = useState<string | null>(null);
  const [jumpValue, setJumpValue] = useState("");
  const reqId = useRef(0);

  // provider (lowercased) -> ordered list of prioritised game uuids
  const [priorityMap, setPriorityMap] = useState<Record<string, string[]>>({});

  const fetchPriorities = useCallback(async () => {
    try {
      const res = await apiFetch<PrioritiesResponse>("/api/gis/admin/gis/priorities");
      const map: Record<string, string[]> = {};
      (res?.data || []).forEach((row) => { map[row.vendor.toLowerCase()] = row.gameIds; });
      setPriorityMap(map);
    } catch {
      setPriorityMap({});
    }
  }, []);

  useEffect(() => { fetchPriorities(); }, [fetchPriorities]);

  const isPrioritized = (g: Game) => {
    const list = priorityMap[(g.provider || "").toLowerCase()];
    return !!list && list.includes(g.uuid);
  };

  /* providers for the filter dropdown (once) */
  useEffect(() => {
    apiFetch<{ data: { name: string }[] }>("/api/gis/providersgis")
      .then((r) => {
        const names = Array.isArray(r?.data) ? r.data.map((p) => p.name).filter(Boolean) : [];
        setProviders(names.sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => setProviders([]));
  }, []);

  /* debounce the search box */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  /* reset to page 1 whenever a filter changes */
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, provider, status]);

  const fetchGames = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (provider) qs.set("provider", provider);
      if (debouncedSearch) qs.set("search", debouncedSearch);
      if (status) qs.set("hidden", status);
      const res = await apiFetch<GamesResponse>(`/api/gis/admin/gis/games?${qs.toString()}`);
      if (id !== reqId.current) return; // a newer request superseded this one
      setGames(Array.isArray(res?.data) ? res.data : []);
      setTotalPages(res?.pagination?.totalPages || 1);
      setTotalItems(res?.pagination?.totalItems || 0);
    } catch (err: any) {
      if (id !== reqId.current) return;
      setError(err?.message || "Failed to load games");
      setGames([]);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [page, provider, debouncedSearch, status]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  /* flip a single game's visibility (optimistic, revert on failure) */
  const toggle = async (g: Game) => {
    const nextHidden = !g.is_hidden;
    setSavingUuid(g.uuid);
    setGames((prev) => prev.map((x) => (x.uuid === g.uuid ? { ...x, is_hidden: nextHidden } : x)));
    try {
      await apiFetch(`/api/gis/admin/gis/games/${encodeURIComponent(g.uuid)}/visibility`, {
        method: "PATCH",
        body: JSON.stringify({ hidden: nextHidden }),
      });
      // if a status filter is active the row may no longer belong on this page
      if (status && ((status === "true") !== nextHidden)) {
        setGames((prev) => prev.filter((x) => x.uuid !== g.uuid));
        setTotalItems((n) => Math.max(0, n - 1));
      }
    } catch (err: any) {
      // revert
      setGames((prev) => prev.map((x) => (x.uuid === g.uuid ? { ...x, is_hidden: g.is_hidden } : x)));
      setError(`Couldn't update "${g.name}": ${err?.message || "request failed"}`);
    } finally {
      setSavingUuid(null);
    }
  };

  /* prioritise / unprioritise a game within its OWN provider's section.
     Optimistic: updates the local priority map immediately, reverts on failure. */
  const togglePriority = async (g: Game) => {
    const vendorKey = (g.provider || "").toLowerCase();
    const next = !isPrioritized(g);
    setSavingPriorityUuid(g.uuid);
    setPriorityMap((prev) => {
      const list = prev[vendorKey] || [];
      const nextList = next ? [...list, g.uuid] : list.filter((id) => id !== g.uuid);
      return { ...prev, [vendorKey]: nextList };
    });
    try {
      await apiFetch(`/api/gis/admin/gis/games/${encodeURIComponent(g.uuid)}/priority`, {
        method: "PATCH",
        body: JSON.stringify({ prioritized: next }),
      });
    } catch (err: any) {
      // revert
      setPriorityMap((prev) => {
        const list = prev[vendorKey] || [];
        const revertedList = next ? list.filter((id) => id !== g.uuid) : [...list, g.uuid];
        return { ...prev, [vendorKey]: revertedList };
      });
      setError(`Couldn't ${next ? "prioritise" : "unprioritise"} "${g.name}": ${err?.message || "request failed"}`);
    } finally {
      setSavingPriorityUuid(null);
    }
  };

  const goToPage = () => {
    const n = parseInt(jumpValue, 10);
    if (!isNaN(n)) setPage(Math.min(totalPages, Math.max(1, n)));
    setJumpValue("");
  };

  const resetFilters = () => {
    setSearch("");
    setProvider("");
    setStatus("");
    setPage(1);
  };

  return (
    <div className="p-4 ml-0 lg:ml-[256px] space-y-5">
      {/* ── header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">Game Visibility</h1>
          <p className="text-sm text-gray-300 mt-1">
            Hide or show individual games across <span className="font-medium">growl.games</span>.
            Hidden games disappear from the homepage, Explore, provider and search listings.
          </p>
        </div>
        <button
          onClick={() => { fetchGames(); fetchPriorities(); }}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* ── filters ── */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search game name…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
          />
        </div>

        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          className="py-2 px-3 rounded-lg border border-gray-300 text-sm bg-white min-w-[180px]"
        >
          <option value="">All providers</option>
          {providers.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as "" | "false" | "true")}
          className="py-2 px-3 rounded-lg border border-gray-300 text-sm bg-white"
        >
          <option value="">All statuses</option>
          <option value="false">Visible only</option>
          <option value="true">Hidden only</option>
        </select>

        {(search || provider || status) && (
          <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline px-2 py-2">
            Clear
          </button>
        )}

        <div className="ml-auto text-sm text-gray-500 whitespace-nowrap">
          {totalItems.toLocaleString()} game{totalItems === 1 ? "" : "s"}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      {/* ── grid ── */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 text-gray-300 py-24">
          <Loader2 size={22} className="animate-spin" /> Loading games…
        </div>
      ) : games.length === 0 ? (
        <div className="text-center text-gray-300 py-24">
          <p className="text-lg font-medium">No games found</p>
          <p className="text-sm mt-1">Try a different provider, status or search term.</p>
        </div>
      ) : (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(190px,1fr))]">
          {games.map((g) => {
            const hidden = !!g.is_hidden;
            const prioritized = isPrioritized(g);
            const saving = savingUuid === g.uuid;
            const savingPriority = savingPriorityUuid === g.uuid;
            return (
              <div
                key={g.uuid}
                className={`bg-white rounded-xl overflow-hidden shadow-sm border transition ${
                  prioritized ? "border-rose-400 ring-1 ring-rose-300" : "border-transparent"
                } ${hidden ? "opacity-70" : ""}`}
              >
                <div className="relative aspect-[3/2] bg-gray-100">
                  <img
                    src={gameImage(g)}
                    alt={g.name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG;
                    }}
                    className={`absolute inset-0 w-full h-full object-cover ${hidden ? "grayscale" : ""}`}
                  />
                  {hidden && (
                    <span className="absolute top-2 left-2 text-[10px] font-bold tracking-wide uppercase px-2 py-1 rounded-full bg-black/70 text-white">
                      Hidden
                    </span>
                  )}
                  <button
                    onClick={() => togglePriority(g)}
                    disabled={savingPriority}
                    title={
                      prioritized
                        ? `Prioritised in ${g.provider} — click to remove`
                        : `Prioritise (show first in ${g.provider})`
                    }
                    aria-label={prioritized ? "Remove priority" : "Prioritise game"}
                    className={`absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full backdrop-blur transition disabled:opacity-60 ${
                      prioritized ? "bg-rose-500 text-white shadow" : "bg-black/45 text-white hover:bg-black/65"
                    }`}
                  >
                    {savingPriority ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <Heart size={15} fill={prioritized ? "currentColor" : "none"} />
                    )}
                  </button>
                </div>

                <div className="p-3">
                  <div
                    className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem]"
                    title={g.name}
                  >
                    {g.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">
                    {g.label || g.provider}
                  </div>

                  <button
                    onClick={() => toggle(g)}
                    disabled={saving}
                    className={`mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition disabled:opacity-60 ${
                      hidden
                        ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    }`}
                  >
                    {saving ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : hidden ? (
                      <EyeOff size={15} />
                    ) : (
                      <Eye size={15} />
                    )}
                    {hidden ? "Hidden — show it" : "Visible — hide it"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── pagination ── */}
      {!loading && games.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white text-gray-700 shadow-sm disabled:opacity-40 hover:bg-gray-50"
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <span className="text-sm text-gray-300">
            Page <span className="font-semibold text-white">{page}</span> of {totalPages.toLocaleString()}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white text-gray-700 shadow-sm disabled:opacity-40 hover:bg-gray-50"
          >
            Next <ChevronRight size={16} />
          </button>

          {/* skip directly to a page */}
          <form onSubmit={(e) => { e.preventDefault(); goToPage(); }} className="flex items-center gap-1 ml-1">
            <input
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value.replace(/[^0-9]/g, ""))}
              inputMode="numeric"
              placeholder={`1–${totalPages}`}
              aria-label="Go to page"
              className="w-24 px-2 py-2 rounded-lg text-sm text-gray-700 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button type="submit" className="px-3 py-2 rounded-lg bg-white text-gray-700 shadow-sm hover:bg-gray-50 text-sm">
              Go
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
